import { StreamedImage } from "types/uiTypes";

export enum TestCategory {
  VisualField = "VFA",
  FDT = "FDT",
  ContrastSensitivity = "CS",
  VisualAcuity = "VA",
  ColorVision = "CV",
  AmslerGrid = "AG",
  Pupillometry = "PL",
  Dyslexia = "DY",
  ReadingAssessment = "RA",
  ContinuousPerformance = "CPT",
  OcularMotility = "OM",
  Cover = "COV",
  StereoAcuity = "SA",
  Videonystagmography = "VSG",
  Kinetic = "KIN",
  Dyscalculia = "DC",
  WisconsinCardSorting = "WCST",
  BinocularVision = "BVT",
  OptokineticNystagmus = "OKN",
}

export enum TestType {
  Threshold = "threshold",
  Ascending = "ascending",
  PelliRobson = "pelliRobson",
  TumblingE = "tumblingE",
  TumblingEThreshold = "tumblingEThreshold",
  ReadingAssessment = "readingAssessment",
  ContinuousPerformance = "continuousPerformance",
  GoNoGo = "goNoGo",
  IdenticalPairs = "identicalPairs",
  Logmar = "logmar",
  Chart = "chart",
  Pupillometry = "pupillometry",
  RAPD = "RAPD",
  CircularPursuit = "circularPursuit",
  XIH = "xih",
  SmoothPursuit = "smoothPursuit",
  C_20_1 = "C-20-1",
  C_20_5 = "C-20-5",
  N_30_1 = "N-30-1",
  N_30_5 = "N-30-5",
  CoverUncover = "coverUncover",
  ObjectivePhoria = "objectivePhoria",
  Staircase = "staircase",
  Videonystagmography = "videonystagmography",
  AutoKinetic = "autoKinetic",
  PER_24_2 = "24-2",
  PER_24_2C = "24-2C",
  PER_30_2 = "30-2",
  PER_10_2 = "10-2",
  PER_BINOCULAR = "BINOCULAR",
  PER_BINOCULAR2 = "BINOCULAR2",
  PER_BINOCULAR3 = "BINOCULAR3",
  PER_C_40 = "C-40",
  PER_C_76 = "C-76",
  PER_SUPERIOR36 = "SUPERIOR36",
  PER_SUPERIOR64 = "SUPERIOR64",
  PER_FF_120 = "FF-120",
  VisualField = "VisualField",
  FDT = "FDT",
  ContrastSensitivity = "ContrastSensitivity",
  ColorVision = "ishihara",
  ColorVisionAutomatic = "ishiharaAutomatic",
  VisualAcuity = "VisualAcuity",
  NumericalCognition = "numericalCognition",
  WCST = "WCST",
  NearPointConvergence = "nearPointConvergence",
  AntiSaccades = "antiSaccade",
  OptokineticNystagmus = "optokineticNystagmus",
}

/**
 * Represents the various states of a test within the application workflow.
 *
 * @enum {string}
 * @readonly
 *
 * @property {string} DeviceSelection - Device selection step. User can select a device and
 *                                      proceed to the next step.
 * @property {string} PatientSelection - Patient selection step. User can select a patient and
 *                                       proceed to the next step.
 * @property {string} TestConfiguration - Test configuration step. User can configure the test settings.
 *                                      This is the last step before starting the test.
 * @property {string} StartRequested - Test configurations are set and sent to the backend.
 * @property {string} Started - Test start message received from backend, indicating we can
 *                             show relevant progress components.
 * @property {string} Calibration - Reserved for separating eye calibration from the actual test.
 *                                 Currently not used.
 * @property {string} CompleteRequested - Test completion requested by the user. (Upon successful
 *                                        completion of the test, this state is set to TestStatus.Complete.)
 * @property {string} Complete - Test is completed. Can be triggered by backend or frontend.
 * @property {string} TerminateRequested - Test termination requested by the user. (Upon successful
 *                                        completion of the test, this state is set back to TestStatus.Configuration.)
 * @property {string} Error - Error state. Currently not used.
 */

export enum TestStatus {
  DeviceSelection = "deviceSelection",
  PatientSelection = "patientSelection",
  TestConfiguration = "configuration",
  StartRequested = "startRequested",
  Calibration = "calibration",
  VirtualAssistant = "virtualAssistant",
  Started = "started",
  Paused = "paused",
  CompleteRequested = "completeRequested",
  Completed = "completed",
  WaitingForNextEye = "waitingForNextEye",
  TerminateRequested = "terminateRequested",
  Error = "error",
}

export const TEST_IN_PROGRESS_STATUSES: ReadonlySet<TestStatus> = new Set([
  TestStatus.StartRequested,
  TestStatus.Calibration,
  TestStatus.VirtualAssistant,
  TestStatus.Started,
  TestStatus.Paused,
  TestStatus.CompleteRequested,
  TestStatus.Completed,
  TestStatus.WaitingForNextEye,
  TestStatus.TerminateRequested,
]);

export enum TestLanguage {
  NOT_SPECIFIED = "NOT_SPECIFIED",
  English = "en",
  Turkish = "tr",
  Arabic = "ar",
  French = "fr",
  Czech = "cs",
  German = "de",
  Greek = "el",
  Spanish = "es",
  Persian = "fa",
  Italian = "it",
  Japanese = "ja",
  Korean = "ko",
  Malay = "ms",
  Romanian = "ro",
  Swedish = "sv",
  Urdu = "ur",
  Vietnamese = "vi",
  Chinese = "zh",
  Bulgarian = "bg",
  Danish = "da",
  Hungarian = "hu",
  NorwegianBokmal = "nb",
  Dutch = "nl",
  Polish = "pl",
  Portuguese = "pt",
  Russian = "ru",
  Thai = "th",
  Latvian = "lv",
  Finnish = "fi",
  Indonesian = "id",
}

export class LanguageData {
  languageCode: TestLanguage;
  regionCode?: string;
  orientation: TextOrientation;
  translationKey: string;
}

export enum EyeType {
  BothEye = "bothEye",
  RightEye = "rightEye",
  LeftEye = "leftEye",
  RightThenLeft = "rightThenLeft",
}

export enum Direction {
  UP = "UP",
  DOWN = "DOWN",
  LEFT = "LEFT",
  RIGHT = "RIGHT",
}

export enum TextOrientation {
  L2R = "L2R",
  R2L = "R2L",
}

export enum SnellenSize {
  SIZE_20_200 = "SIZE_20_200",
  SIZE_20_100 = "SIZE_20_100",
  SIZE_20_80 = "SIZE_20_80",
  SIZE_20_63 = "SIZE_20_63",
  SIZE_20_50 = "SIZE_20_50",
  SIZE_20_40 = "SIZE_20_40",
  SIZE_20_32 = "SIZE_20_32",
  SIZE_20_25 = "SIZE_20_25",
  SIZE_20_20 = "SIZE_20_20",
}

export enum GoldmannSize {
  GOLDMANN_III = 3,
  GOLDMANN_IV = 4,
  GOLDMANN_V = 5,
}

export enum FDTBehaviourType {
  SupraThreshold = "supraThreshold",
  Threshold = "threshold",
}

export enum WaitDuration {
  Standard = 400,
  Slow = 600,
  Slower = 800,
}

export interface EyeImageData extends StreamedImage {
  eyeType: EyeType;
}

export interface Position {
  x: number;
  y: number;
}
export interface PercentagePoint {
  xPercentage: number;
  yPercentage: number;
  timeStamp?: number;
  gazeDelta?: number;
  pageIndex?: number;
}

export interface DistanceData {
  distance: number;
  timeStamp: number;
}

export interface PercentageWithTimeStamp extends PercentagePoint {
  timeStamp?: number;
}
export interface PositionWithValue extends Position {
  value: number;
}

export interface GazeData {
  // Headset sends the sample timestamp at the top level (deciseconds = seconds×10),
  // not inside the individual gaze positions. Capital "T" matches the wire/DB field.
  Timestamp?: number;
  leftGazePosition?: PositionWithTimeStamp;
  rightGazePosition?: PositionWithTimeStamp;
  combinedGazePosition?: PositionWithTimeStamp;
}
export interface PositionWithTimeStamp extends Position {
  timestamp?: number;
}
export interface PupilSizeData {
  leftPupilSize: number | null;
  rightPupilSize: number | null;
  timeStamp: number;
}
// ------- Dyslexia Specific Data

export interface GazeDirectionVector {
  x: number;
  y: number;
  z: number;
}

export interface GazeWithDelta {
  gazePercentageData: PercentagePoint;
  delta: number;
  gazeDirectionVector?: GazeDirectionVector;
  leftGazeDirectionVector?: GazeDirectionVector;
  rightGazeDirectionVector?: GazeDirectionVector;
  gazeOrigin?: GazeDirectionVector;
  headPosition?: GazeDirectionVector;
  headAngularSpeed?: number;
  leftEyeOpenness?: number;
  rightEyeOpenness?: number;
  leftPupilMm?: number;
  rightPupilMm?: number;
}
// Commonly Fetched Data Types

export type Velocity = { timeStamp: number; velocity: number };
export type HeadVelocity = Velocity;
export type EyeDisplacement = Velocity;
